import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATTRAPPE, Fehler, hole, sende } from "./klient";
/* Die Einsätze des Prototyps liegen neben den übrigen Beispieldaten, aber in
   einer eigenen Datei: Sie werden von zwei Modulen gelesen — hier und von der
   Tagesmappe in `routen.ts` — und von diesem verändert. Ein Stand an zwei
   Stellen läuft auseinander (dieselbe Begründung wie bei `attrappe/aufnahmen.ts`). */
import * as A from "./attrappe/einsaetze";
import { mitMappe } from "./mappe";
import {
  einreihen, useGesendet, useUebertraegtGerade, useWarteschlange, zuruecknehmen,
} from "./warteschlange";
import type { Wartender } from "./warteschlange";
import type { Randtage } from "@mrmachine/fach";

/* ══════════════════════════════════════════════════════════════════
   Die Einsatzlage — was an einem Einsatz gerade ist, am Telefon.

   Luca, 17.09.2026: *„Start trackt die Anfahrt zur Baustelle; ‚Bin auf der
   Baustelle' — dann läuft die Baustellenzeit."* Und: *„Kopfdaten des
   Auftrags, Vorbereitung usw. sind Knöpfe zum Nachsehen."*

   ── Zwei Quellen, eine Lage ──────────────────────────────────────

   **Der Server leitet ab** (`GET /handy/einsatzlage/:belegung`): Zustand,
   Tag x von n, laufende Phase, Einsatztag, letzter Zählerstand — gespeichert
   ist davon nichts.

   **Das Gerät weiß, was noch nicht angekommen ist**: die Anfahrt, die im
   Funkloch begann, das Ende von vorhin, die Bestätigung von heute Abend.
   Beides zusammen ist die **Sicht** (`useEinsatzsicht`) — und der Zustand
   wird aus ihr **mit derselben Reihenfolge** noch einmal abgeleitet wie auf
   dem Server (`zustandAbleiten`). Sonst stünde der Fahrer im Funkloch nach
   „Auf der Baustelle" weiter vor „Anfahrt starten".

   ── Jeder Schritt geht über das Gerät ────────────────────────────

   Losfahren, ankommen, Ende, Zählerstand und Bestätigung gehen **immer**
   durch die Warteschlange, auch mit Netz. Nur so bleibt die Reihenfolge
   wahr — die Ankunft darf nicht vor der Anfahrt beim Server sein —, und nur
   so gibt es „Rückgängig": **zehn Sekunden** bleibt der Phasenwechsel auf
   dem Gerät (`halten`) und lässt sich zurücknehmen, ohne dass der Server
   je davon hört. Rückfragen („Wirklich losfahren?") gibt es dafür keine.

   **Die Uhr des Geräts geht nur mit, wenn es spät wird** (`zeitfeldSpaet`):
   Geht die Eintragung nach dem Fenster gleich hinaus, zählt `now()` der
   Datenbank — zehn Sekunden später, aber ohne „offline" in der Spur.

   **Übernehmen geht nur mit Netz** (`POST /handy/uebernehmen`): Wer im
   Funkloch übernimmt, übernimmt vielleicht, was ein anderer eine Minute
   vorher genommen hat.

   **Regel 9.** Alles hier hängt am Einsatz. Eine Summe je Mensch gibt es
   nicht, auch nicht als Nebenprodukt.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;
const txt = (w: unknown): string => (w === null || w === undefined ? "" : String(w));
const zahlOder = (w: unknown): number | null => {
  if (w === null || w === undefined || w === "") return null;
  const n = typeof w === "number" ? w : Number(w);
  return Number.isFinite(n) ? n : null;
};
const objekt = (w: unknown): Roh => (w && typeof w === "object" && !Array.isArray(w) ? (w as Roh) : {});

export const ZUSTAENDE = [
  "offen", "vorbereiten", "uebernommen", "unterwegs", "vor_ort", "tag_offen", "tag_bestaetigt",
  "abgeschlossen",
] as const;
export type Zustand = (typeof ZUSTAENDE)[number];

/**
 * **Warum hier gerade nichts weitergeht** (18.09.2026) — vom Server abgeleitet
 * (`dienste/einsatzlage.ts`, `hindernisFinden`), hier nur weitergereicht.
 *
 * Luca, am Telefon: *„Es steht ja, aber ich kann auch nur zurück … Ich kann die
 * Anfahrt nicht starten, gar nichts."* Bis dahin blieb in diesem Fall ein
 * einziger Knopf stehen — „Zurück zu Heute" — **ohne ein Wort dazu**. Wer nicht
 * weiterkommt, muss sehen, warum; sonst hält er den Einsatz für kaputt.
 */
export const HINDERNISSE = [
  "laeuft_bei_anderem", "noch_nicht_dran", "einsatz_vorbei",
  "ohne_eigene_belegung", "faehrt_ein_anderer", "tag_ist_zu",
] as const;
export type Hindernis = (typeof HINDERNISSE)[number];

/** Ein Mensch an einer Aufgabe — und seit wann er sie hat (`booking_claim.claimed_at`). */
export type Wer = { id: string; name: string; am?: string | null };

export type Phase = {
  eintragId: string;
  art: "anfahrt" | "arbeit";
  seit: string;
  person: string;
  meins: boolean;
  /** Liegt der Wechsel noch auf dem Gerät? Dann gibt es noch keine ID beim Server. */
  wartet: boolean;
  /** Die Eintragung in der Warteschlange — für „Rückgängig". */
  wartendId: string | null;
};

export type Einsatztag = {
  datum: string;
  sekunden: number;
  eintraege: number;
  zaehlerBeginn: number | null;
  zaehlerEnde: number | null;
  bestaetigtAm: string | null;
  bestaetigtVon: string;
  offlineBestaetigt: boolean;
  /**
   * Warum der Kunde den Tag nicht bestätigt hat (18.09.2026). Steht er da,
   * ist der Tag zu — bestätigt ist er nicht, das holt die Disposition nach.
   *
   * Beim Server `assignment.signature_missing_reason` (0132): **Er steht fest,
   * sobald er da ist**, und bleibt auch neben einer späteren Bestätigung
   * stehen. Deshalb gewinnt auf dem Gerät wie beim Server der erste Grund
   * (`sichtBauen`), und `bestaetigtAm` schlägt ihn in der Anzeige.
   */
  ohneBestaetigungGrund: string | null;
};

export type Einsatzlage = {
  /** Die Belegung der Maschine — daran hängen Protokoll, Fotos, Aufgaben. */
  belegungId: string;
  /** Die eigene Bediener-Belegung — daran hängt die Zeit. Leer, wenn ich nicht fahre. */
  eigeneBelegungId: string | null;
  /**
   * Ich stehe an dieser Position als Bediener — **auch wenn `eigeneBelegungId`
   * fehlt** (18.09.2026). Bindung und Belegung sind zwei Dinge: Verschwindet die
   * Belegung des Menschen und die Bindung bleibt stehen (Optionsverfall 0130,
   * „Belegung entfernen" im Brett), ist der Fahrer am Telefon von jedem Schritt
   * abgeschnitten. Daran ändert dieses Feld nichts — aber es lässt den Satz
   * dazu entstehen (`hindernis`).
   */
  ichFahre: boolean;
  /**
   * **Nimmt der Server heute eine Zeit an?** (P9-01, 05.10.2026) Die eigene
   * Belegung liegt auf heute — dieselbe Bedingung, mit der `POST
   * /handy/einsatz/angekommen` sonst `zeit.nicht_heute` sagt. Bei einem
   * Einsatz aus der Vorwoche bot der Schirm „Ohne Anfahrt beginnen" an, und
   * der Schritt kam abgewiesen zurück. Fehlt das Feld (ältere Lage im
   * Speicher), gilt es als erlaubt — wie bisher.
   */
  heuteStempelbar: boolean;
  /** Der Grund, wenn nichts weitergeht — angezeigt nur, wenn kein Schritt übrig ist. */
  hindernis: { grund: Hindernis; wer: string | null } | null;
  kennung: string;
  typ: string;
  vorgang: string;
  kunde: string;
  /** Der Hinweis für den Hof am Vorgang (0284) — leer: keiner, kein Kasten. */
  hofhinweis?: string;
  /**
   * **Abholung am Vorabend, Rückgabe am Morgen** (0286): wann der Rand nicht
   * berechnet würde — mit der Einstellung von jetzt. Das Protokollblatt sagt
   * es dem Fahrer vor der Unterschrift („wird nicht berechnet"). Fehlt es
   * (ältere Lage im Speicher, Stand vor 0286), steht nichts da.
   */
  randtage?: Randtage;
  zustand: Zustand;
  mitBediener: boolean;
  unterschriftNoetig: boolean;
  /**
   * **Gezählter Bestand** (Migration 0194): wie viele Stück die Belegung hält.
   * Dann fragt das Protokoll, wie viele wirklich hinaus- oder zurückgingen.
   * `null` am Einzelstück — dort gibt es die Frage nicht.
   */
  stueckGeplant: number | null;
  /**
   * Entsteht an dieser Position ein Übergabeprotokoll? (0126)
   *
   * Beim reinen Mietgerät immer; beim Einsatz mit Fahrer nur, wenn der Betrieb
   * es eingeschaltet hat (Einstellungen → Betrieb). Ist es `false`, führt der
   * Weg direkt zur Zeit und zur Bestätigung am Ende des Tages — und das Ist
   * setzt nicht das Protokoll, sondern das erste „Auf der Baustelle" und die
   * Bestätigung des letzten Einsatztags. **Der Server entscheidet das**, nicht
   * die Maske: Sonst zeigte sie einen Schritt, den er nicht erwartet.
   */
  uebergabeNoetig: boolean;
  /** `bis` ist der geplante Schluss als Kalendertag (B1) — was beim vorzeitigen Abschluss wegfällt. */
  tag: { nr: number | null; gesamt: number; datum: string | null; bis: string | null };
  phase: Phase | null;
  einsatztag: Einsatztag | null;
  letzterZaehler: { stand: number; quelle: string; am: string | null } | null;
  vorbereitung: { erledigt: boolean; genommen: Wer | null };
  uebergabe: { erledigt: boolean; genommen: Wer | null };
  ruecknahme: { erledigt: boolean; genommen: Wer | null };
};

function alsWer(w: unknown): Wer | null {
  const o = objekt(w);
  return txt(o["id"]) ? { id: txt(o["id"]), name: txt(o["name"]), am: txt(o["am"]) || null } : null;
}

/** `randtage` aus der Lage (0286) — was nicht vollständig ist, gilt als nicht da. */
function alsRandtage(wert: unknown): Randtage {
  const o = objekt(wert);
  const va = o["vorabend"] ? objekt(o["vorabend"]) : null;
  const mo = o["morgen"] ? objekt(o["morgen"]) : null;
  return {
    vorabend: va && txt(va["tag"]) && txt(va["ab"]) && txt(va["beginn"])
      ? { tag: txt(va["tag"]), ab: txt(va["ab"]), beginn: txt(va["beginn"]) } : null,
    morgen: mo && txt(mo["tag"]) && txt(mo["bis"]) && txt(mo["ende"])
      ? { tag: txt(mo["tag"]), bis: txt(mo["bis"]), ende: txt(mo["ende"]) } : null,
  };
}

export function alsEinsatzlage(roh: Roh): Einsatzlage {
  const tag = objekt(roh["tag"]);
  const phase = roh["phase"] ? objekt(roh["phase"]) : null;
  const et = roh["einsatztag"] ? objekt(roh["einsatztag"]) : null;
  const lz = roh["letzter_zaehler"] ? objekt(roh["letzter_zaehler"]) : null;
  const hi = roh["hindernis"] ? objekt(roh["hindernis"]) : null;
  const grund = hi ? HINDERNISSE.find((h) => h === hi["grund"]) ?? null : null;
  const aufgabe = (name: string) => {
    const a = objekt(roh[name]);
    return { erledigt: a["erledigt"] === true, genommen: alsWer(a["genommen"]) };
  };
  return {
    belegungId: txt(roh["belegung_id"]),
    eigeneBelegungId: txt(roh["eigene_belegung_id"]) || null,
    ichFahre: roh["ich_fahre"] === true,
    heuteStempelbar: roh["heute_stempelbar"] !== false,
    hindernis: grund ? { grund, wer: txt(hi?.["wer"]) || null } : null,
    kennung: txt(roh["kennung"]),
    typ: txt(roh["typ"]),
    vorgang: txt(roh["vorgang"]),
    kunde: txt(roh["kunde"]),
    ...(txt(roh["hofhinweis"]) ? { hofhinweis: txt(roh["hofhinweis"]) } : {}),
    ...(roh["randtage"] ? { randtage: alsRandtage(roh["randtage"]) } : {}),
    zustand: ZUSTAENDE.find((z) => z === roh["zustand"]) ?? "offen",
    mitBediener: roh["mit_bediener"] === true,
    unterschriftNoetig: roh["unterschrift_noetig"] !== false,
    stueckGeplant: zahlOder(roh["stueck_geplant"]),
    uebergabeNoetig: roh["uebergabe_noetig"] !== false,
    tag: {
      nr: zahlOder(tag["nr"]), gesamt: zahlOder(tag["gesamt"]) ?? 1,
      datum: txt(tag["datum"]) || null, bis: txt(tag["bis"]) || null,
    },
    phase: phase ? {
      eintragId: txt(phase["eintrag_id"]),
      art: phase["art"] === "anfahrt" ? "anfahrt" : "arbeit",
      seit: txt(phase["seit"]),
      person: txt(phase["person"]),
      meins: phase["meins"] === true,
      wartet: false,
      wartendId: null,
    } : null,
    einsatztag: et ? {
      datum: txt(et["datum"]),
      sekunden: zahlOder(et["sekunden"]) ?? 0,
      eintraege: zahlOder(et["eintraege"]) ?? 0,
      zaehlerBeginn: zahlOder(et["zaehler_beginn"]),
      zaehlerEnde: zahlOder(et["zaehler_ende"]),
      bestaetigtAm: txt(et["bestaetigt_am"]) || null,
      bestaetigtVon: txt(et["bestaetigt_von"]),
      offlineBestaetigt: et["offline_bestaetigt"] === true,
      ohneBestaetigungGrund: txt(et["ohne_bestaetigung_grund"]) || null,
    } : null,
    letzterZaehler: lz && zahlOder(lz["stand"]) !== null ? {
      stand: zahlOder(lz["stand"]) ?? 0, quelle: txt(lz["quelle"]), am: txt(lz["am"]) || null,
    } : null,
    vorbereitung: aufgabe("vorbereitung"),
    uebergabe: aufgabe("uebergabe"),
    ruecknahme: aufgabe("ruecknahme"),
  };
}

/** Ein Wimpernschlag Verzögerung wie in `routen.ts` — sonst blitzt der
 *  Ladezustand nie auf, und man baut ihn versehentlich falsch. */
const gleich = <T>(wert: T, ms = 120): Promise<T> =>
  new Promise((fertig) => setTimeout(() => fertig(wert), ms));

/**
 * **Seit dem 18.09.2026 steht hier eine Lage und nicht mehr `404`.**
 *
 * Bis dahin warf diese Funktion unter `VITE_ATTRAPPE=1` bei **jedem** Einsatz
 * „nicht gefunden" — auf jedem Einsatzschirm stand „Diesen Einsatz gibt es
 * nicht mehr", ausgerechnet auf dem Schirm, an dem an diesem Tag am meisten
 * gearbeitet wurde. Die Lagen und ihr Stand liegen in
 * [`attrappe/einsaetze.ts`](attrappe/einsaetze.ts); dieselbe Datei füllt die
 * Tagesmappe, damit beide dieselben Belegungen meinen.
 */
export async function einsatzlage(belegung: string): Promise<Einsatzlage> {
  if (ATTRAPPE) {
    const lage = A.eineLage(belegung);
    /* Eine Belegung, die es nicht gibt, bleibt ein `404`: Der Schirm sagt
       dann „Diesen Einsatz gibt es nicht mehr", und das ist richtig — falsch
       war, dass er es über jeden Einsatz sagte. */
    if (!lage) throw new Fehler("nicht_gefunden", 404);
    return gleich(lage);
  }
  return alsEinsatzlage(await hole<Roh>(`/handy/einsatzlage/${encodeURIComponent(belegung)}`));
}

export const lageSchluessel = {
  /* Unter `handy`, damit das Auffrischen des Tages sie mitnimmt. */
  lage: (belegung: string) => ["handy", "einsatzlage", belegung] as const,
  verlauf: (belegung: string) => ["handy", "verlauf", belegung] as const,
};

/** Die Lage — über die Mappe: Wer den Einsatz einmal offen hatte, hat ihn auch im Funkloch. */
export const useEinsatzlage = (belegung: string) =>
  useQuery({
    queryKey: lageSchluessel.lage(belegung),
    queryFn: () => mitMappe(`handy/einsatzlage/${belegung}`, () => einsatzlage(belegung)),
    enabled: !!belegung,
  });

/* ══════════════ Die Sicht: Server und Gerät zusammen ══════════════ */

/** Gehört diese Eintragung zu diesem Einsatz? Über die Maschine oder die eigene Belegung. */
function gehoertDazu(w: Wartender, lage: Einsatzlage): boolean {
  const b = txt(w.nutzlast["belegung_id"]);
  return b !== "" && (b === lage.belegungId || b === lage.eigeneBelegungId);
}

/** Welcher Weg welcher Schritt ist. */
const WEG = {
  losfahren: "/handy/einsatz/losfahren",
  angekommen: "/handy/einsatz/angekommen",
  ausstempeln: "/handy/einsatz/ausstempeln",
  zaehler: "/handy/einsatz/zaehler",
  quittung: "/zeiten/quittung",
  ohneBestaetigung: "/zeiten/ohne-bestaetigung",
  protokoll: "/protokolle",
} as const;

export type Sicht = Einsatzlage & {
  /** Was vom Einsatz noch auf dem Gerät liegt — für die Marke „wartet". */
  wartet: number;
  /** Ein Schritt, den der Server abgewiesen hat — er steht, bis ein Mensch ihn wegnimmt. */
  abgewiesen: Wartender | null;
  /** Ist das Übergabeprotokoll nur auf dem Gerät? */
  uebergabeWartet: boolean;
  ruecknahmeWartet: boolean;
};

/** Entsteht an dieser Position überhaupt ein Protokoll? (0126) */
const mitProtokoll = (s: Pick<Einsatzlage, "mitBediener" | "uebergabeNoetig">): boolean =>
  !s.mitBediener || s.uebergabeNoetig;

/**
 * **Die Vorbereitung ist dran, solange die Maschine nicht draußen ist.**
 *
 * Ohne Protokoll (0126) heißt „draußen": der erste Tag hat begonnen — am
 * zweiten fragt der Schirm nicht mehr danach. **Dieselbe Bedingung steht im
 * Server** (`dienste/einsatzlage.ts`, `vorbereitungOffen`); sie entscheidet
 * dort über den Zustand `vorbereiten` und hier über die Hauptaktion.
 */
export function vorbereitungDran(s: Pick<Einsatzlage,
  "mitBediener" | "uebergabeNoetig" | "tag" | "vorbereitung" | "uebergabe">): boolean {
  return !s.vorbereitung.erledigt && !s.uebergabe.erledigt
    && (mitProtokoll(s) || s.tag.nr === null || s.tag.nr <= 1);
}

/**
 * Der Zustand aus den Teilen — **dieselbe Reihenfolge wie `dienste/einsatzlage.ts`.**
 *
 * Wer das eine ändert, ändert das andere: Sonst zeigt das Telefon im Funkloch
 * einen Zustand, den es beim Server nicht gibt.
 *
 * **`vorbereiten` ist seit dem 18.09.2026 ein eigener Zustand**, weil die
 * Vorbereitung kein Posten neben dem Einsatz ist, sondern sein erster Stand
 * (Luca: *„Ich habe einen Vorgang mit dem Status ‚vorbereiten', vorher kann
 * ich gar nicht losfahren"*).
 */
export function zustandAbleiten(s: Pick<Einsatzlage,
  "phase" | "einsatztag" | "mitBediener" | "uebergabeNoetig" | "tag"
  | "vorbereitung" | "uebergabe" | "ruecknahme">): Zustand {
  if (s.ruecknahme.erledigt) return "abgeschlossen";
  if (s.phase?.art === "anfahrt") return "unterwegs";
  if (s.phase?.art === "arbeit") return "vor_ort";
  if (s.einsatztag?.bestaetigtAm) return "tag_bestaetigt";
  if (s.mitBediener && (s.uebergabe.erledigt || (s.einsatztag?.eintraege ?? 0) > 0)) return "tag_offen";
  if (!s.mitBediener && s.uebergabe.erledigt) return "vor_ort";
  if (s.vorbereitung.genommen || s.uebergabe.genommen) {
    return vorbereitungDran(s) ? "vorbereiten" : "uebernommen";
  }
  return "offen";
}

/** Legt die Eintragungen vom Gerät über die Lage des Servers — in ihrer Reihenfolge. */
export function sichtBauen(lage: Einsatzlage, eintraege: Wartender[], lageStand: number): Sicht {
  let phase = lage.phase;
  let tag: Einsatztag | null = lage.einsatztag;
  const uebergabe = { ...lage.uebergabe };
  const ruecknahme = { ...lage.ruecknahme };
  let uebergabeWartet = false;
  let ruecknahmeWartet = false;
  let wartet = 0;
  let abgewiesen: Wartender | null = null;
  const heute = lage.tag.datum ?? "";
  const leererTag = (): Einsatztag => ({
    datum: heute, sekunden: 0, eintraege: 0, zaehlerBeginn: null, zaehlerEnde: null,
    bestaetigtAm: null, bestaetigtVon: "", offlineBestaetigt: false, ohneBestaetigungGrund: null,
  });

  for (const w of eintraege) {
    // Was schon beim Server ist UND in der Lage steht, zählt nicht doppelt.
    if ("gesendetAm" in w && typeof w.gesendetAm === "number" && w.gesendetAm <= lageStand) continue;
    if (!gehoertDazu(w, lage)) continue;
    if (w.abgewiesen) {
      abgewiesen = abgewiesen ?? w;
      continue;
    }
    if (!("gesendetAm" in w)) wartet += 1;
    const wo = w.weg;
    if (wo === WEG.losfahren) {
      phase = {
        eintragId: "", art: "anfahrt", seit: w.erfasstAm, person: "", meins: true,
        wartet: !("gesendetAm" in w), wartendId: w.id,
      };
    } else if (wo === WEG.angekommen) {
      phase = {
        eintragId: "", art: "arbeit", seit: w.erfasstAm, person: "", meins: true,
        wartet: !("gesendetAm" in w), wartendId: w.id,
      };
      tag = { ...(tag ?? leererTag()) };
      tag.eintraege += 1;
    } else if (wo === WEG.ausstempeln) {
      if (phase?.art === "arbeit") {
        const sek = Math.max(0, (new Date(w.erfasstAm).getTime() - new Date(phase.seit).getTime()) / 1000);
        tag = { ...(tag ?? leererTag()) };
        // Die laufende Arbeit steht beim Server noch nicht in den Sekunden des Tages.
        tag.sekunden += sek;
        tag.eintraege = Math.max(1, tag.eintraege);
        phase = null;
      }
    } else if (wo === WEG.zaehler) {
      tag = { ...(tag ?? leererTag()) };
      const b = zahlOder(w.nutzlast["zaehler_beginn"]);
      const e = zahlOder(w.nutzlast["zaehler_ende"]);
      if (b !== null) tag.zaehlerBeginn = b;
      if (e !== null) tag.zaehlerEnde = e;
    } else if (wo === WEG.quittung) {
      tag = { ...(tag ?? leererTag()) };
      tag.bestaetigtAm = w.erfasstAm;
      tag.bestaetigtVon = txt(w.nutzlast["unterschrieben_von"]);
      tag.offlineBestaetigt = !("gesendetAm" in w);
    } else if (wo === WEG.ohneBestaetigung) {
      tag = { ...(tag ?? leererTag()) };
      // Der erste Grund bleibt — wie beim Server (`tagOhneBestaetigung`, Riegel
      // `tagesgrund_steht_fest`, 0132). An einen schon bestätigten Tag kommt
      // keiner: Der Server weist ihn dort ab (`zeit.schon_quittiert`).
      if (!tag.bestaetigtAm) {
        tag.ohneBestaetigungGrund = tag.ohneBestaetigungGrund ?? (txt(w.nutzlast["grund"]) || null);
      }
    } else if (wo === WEG.protokoll) {
      if (w.nutzlast["art"] === "ausgabe") {
        uebergabe.erledigt = true;
        uebergabeWartet = !("gesendetAm" in w);
      } else if (w.nutzlast["art"] === "ruecknahme") {
        ruecknahme.erledigt = true;
        ruecknahmeWartet = !("gesendetAm" in w);
      }
    }
  }

  const teile = { ...lage, phase, einsatztag: tag, uebergabe, ruecknahme };
  return {
    ...teile,
    zustand: zustandAbleiten(teile),
    wartet,
    abgewiesen,
    uebergabeWartet,
    ruecknahmeWartet,
  };
}

/**
 * Die Sicht eines Einsatzes: Lage vom Server, darüber was auf dem Gerät liegt
 * und was gerade erst angekommen ist.
 */
export function useEinsatzsicht(belegung: string) {
  const abfrage = useEinsatzlage(belegung);
  const wartende = useWarteschlange();
  const gesendet = useGesendet();
  const gerade = useUebertraegtGerade();
  const sicht = useMemo(() => {
    if (!abfrage.data) return null;
    // Erst das Angekommene (älter), dann das Wartende — in der Reihenfolge der Aufnahme.
    const alle = [...gesendet, ...wartende].sort((a, b) => a.reihe - b.reihe);
    return sichtBauen(abfrage.data, alle, abfrage.dataUpdatedAt);
  }, [abfrage.data, abfrage.dataUpdatedAt, wartende, gesendet]);
  return { ...abfrage, sicht, uebertraegtGerade: gerade };
}

/* ══════════════ Der Verlauf ══════════════ */

export type Verlaufsart =
  | "genommen" | "zurueckgegeben" | "vorbereitet" | "losgefahren" | "angekommen" | "beendet"
  | "zaehlerstand" | "foto" | "bestaetigt" | "ohne_bestaetigung" | "uebergeben"
  | "zurueckgenommen" | "schaden" | "notiz" | "versendet" | "korrigiert"
  /* 0280: eine Menge ersetzt; ohne Rechnung abgeschlossen. */
  | "menge_korrigiert" | "ohne_rechnung";

export type Verlaufszeile = {
  art: Verlaufsart;
  am: string;
  wer: string;
  /** Die Belegung, an der die Zeile hängt — für die Zeiten die des Bedieners. */
  belegung: string;
  daten: Roh;
  /** Noch auf dem Gerät — dann steht „wartet" daneben. */
  wartet: boolean;
  /** Mehrere Fotos hintereinander stehen als eine Zeile da. */
  anzahl: number;
  /**
   * Was ein Ende war (18.09.2026): eine Pause — mit `bis`, sobald es
   * weiterging — oder das Ende der Arbeit an einem Tag, der zu ist.
   */
  deutung?: "pause" | "feierabend";
  bis?: string;
};

export async function verlauf(belegung: string): Promise<Verlaufszeile[]> {
  /* Ohne ihn stünde am Fuß des Schirms keine „Zuletzt"-Zeile und im Blatt
     „Verlauf" der Satz, dass noch nichts geschehen sei — an einem Einsatz,
     der seit gestern läuft. */
  if (ATTRAPPE) return gleich(A.verlaufVon(belegung));
  const roh = await hole<Roh>(`/handy/verlauf?belegung=${encodeURIComponent(belegung)}&grenze=40`);
  return (Array.isArray(roh["zeilen"]) ? (roh["zeilen"] as Roh[]) : []).map((z) => ({
    art: txt(z["art"]) as Verlaufsart,
    am: txt(z["am"]),
    wer: txt(z["wer"]),
    belegung: txt(z["belegung_id"]),
    daten: objekt(z["daten"]),
    wartet: false,
    anzahl: 1,
  }));
}

export const useVerlaufRoh = (belegung: string) =>
  useQuery({
    queryKey: lageSchluessel.verlauf(belegung),
    queryFn: () => mitMappe(`handy/verlauf/${belegung}`, () => verlauf(belegung)),
    enabled: !!belegung,
  });

/** Was auf dem Gerät liegt, als Zeile im Verlauf — mit „wartet". */
function ausGeraet(w: Wartender): Verlaufszeile | null {
  const n = w.nutzlast;
  const zeile = (art: Verlaufsart, daten: Roh = {}): Verlaufszeile =>
    ({ art, am: w.erfasstAm, wer: "", belegung: txt(n["belegung_id"]), daten, wartet: true, anzahl: 1 });
  switch (w.weg) {
    case WEG.losfahren: return zeile("losgefahren");
    case WEG.angekommen: return zeile("angekommen");
    case WEG.ausstempeln: return zeile("beendet");
    case WEG.zaehler: return zeile("zaehlerstand", {
      welcher: n["zaehler_ende"] !== undefined ? "ende" : "beginn",
      stand: n["zaehler_ende"] ?? n["zaehler_beginn"],
    });
    case WEG.quittung: return zeile("bestaetigt", { von: n["unterschrieben_von"], offline: true });
    case WEG.ohneBestaetigung: return zeile("ohne_bestaetigung", { grund: n["grund"] });
    case WEG.protokoll: return zeile(n["art"] === "ruecknahme" ? "zurueckgenommen" : "uebergeben", {
      betriebsstunden: n["betriebsstunden"], unterschrieben: !!n["unterschrift_url"] || w.dateien.length > 0,
    });
    case "/fotos": return zeile("foto", { kategorie: n["kategorie"] });
    default: return null;
  }
}

/** Der Kalendertag einer Zeile, wie ihn das Telefon sieht. */
const kalendertag = (iso: string) => new Date(iso).toDateString();

/* Bei gleichem Zeitpunkt: der Abschluss eines Tages über dessen Ende, damit
   „Arbeit beendet" nicht über „Tag bestätigt" steht. */
const RANG: Partial<Record<Verlaufsart, number>> = { bestaetigt: 2, ohne_bestaetigung: 2, beendet: 1 };

/**
 * **Pause oder Feierabend** — abgeleitet, nicht gespeichert (18.09.2026).
 *
 * Luca: *„‚Unterbrechen' — macht daraus ‚Pause' … und im Verlauf steht dann
 * auch ‚Pause'."* Gespeichert ist nur ein Ende (`assignment_time.ends_at`); was
 * es war, sagt, was danach kommt:
 *
 * | ein Ende … | heißt |
 * |---|---|
 * | auf das am selben Tag ein neuer Beginn folgt | „Pause · 12:00–12:30" — der Beginn verschwindet darin |
 * | an einem Tag, der noch offen ist | „Pause" |
 * | das letzte eines bestätigten, ohne Bestätigung geschlossenen oder vergangenen Tages | „Arbeit beendet" |
 *
 * Der Server nennt `weiter_am` und `tag_zu` (`dienste/verlauf.ts`); was noch
 * auf dem Gerät liegt, liest diese Funktion aus der Reihenfolge. `heuteZu`
 * ist der heutige Tag aus der Sicht — bestätigt oder mit Grund geschlossen,
 * auch wenn beides noch in der Warteschlange liegt.
 */
export function pausenDeuten(neuesteZuerst: Verlaufszeile[], heuteZu: boolean): Verlaufszeile[] {
  const auf = [...neuesteZuerst].reverse();
  const weg = new Set<number>();
  const heute = new Date().toDateString();
  auf.forEach((z, i) => {
    if (z.art !== "beendet") return;
    const tag = kalendertag(z.am);
    const gleich = (b: string) => !z.belegung || !b || b === z.belegung;
    const j = auf.findIndex((n, k) => k > i && n.art === "angekommen" && kalendertag(n.am) === tag && gleich(n.belegung));
    const weiter = j >= 0 ? auf[j]?.am : (typeof z.daten["weiter_am"] === "string" ? z.daten["weiter_am"] : null);
    if (weiter) {
      z.deutung = "pause";
      z.bis = weiter;
      if (j >= 0) weg.add(j);
      return;
    }
    // Ein vergangener Tag ist zu, auch ohne Bestätigung — dort geht nichts mehr weiter.
    const zu = z.daten["tag_zu"] === true || (tag === heute ? heuteZu : true);
    z.deutung = zu ? "feierabend" : "pause";
  });
  return auf.filter((_, i) => !weg.has(i)).reverse();
}

/** Der Verlauf eines Einsatzes, neueste zuerst — mit dem, was noch auf dem Gerät liegt. */
export function useVerlauf(lage: (Einsatzlage & { einsatztag: Einsatztag | null }) | null) {
  const abfrage = useVerlaufRoh(lage?.belegungId ?? "");
  const wartende = useWarteschlange();
  const heuteZu = !!(lage?.einsatztag?.bestaetigtAm || lage?.einsatztag?.ohneBestaetigungGrund);
  const zeilen = useMemo(() => {
    if (!lage) return [];
    const geraet = wartende
      .filter((w) => !w.abgewiesen && gehoertDazu(w, lage))
      .map(ausGeraet)
      .filter((z): z is Verlaufszeile => z !== null);
    const alle = [...geraet, ...(abfrage.data ?? [])]
      .map((z) => ({ ...z }))
      .sort((a, b) => b.am.localeCompare(a.am) || (RANG[b.art] ?? 0) - (RANG[a.art] ?? 0));
    // Fotos hintereinander zu einer Zeile — „4 Fotos" liest man, vier Zeilen nicht.
    const zusammen: Verlaufszeile[] = [];
    for (const z of pausenDeuten(alle, heuteZu)) {
      const letzte = zusammen[zusammen.length - 1];
      if (z.art === "foto" && letzte?.art === "foto" && letzte.wartet === z.wartet) {
        letzte.anzahl += 1;
        continue;
      }
      zusammen.push(z);
    }
    return zusammen;
  }, [lage, wartende, abfrage.data, heuteZu]);
  return { zeilen, laedt: abfrage.isPending && !!lage, fehler: abfrage.isError };
}

/* ══════════════ Die Schritte ══════════════ */

/** Wie lange ein Phasenwechsel zurückgenommen werden kann. */
export const RUECKGAENGIG_MS = 10_000;

function useNachEinsatz() {
  const kunde = useQueryClient();
  return () => {
    void kunde.invalidateQueries({ queryKey: ["handy"] });
    void kunde.invalidateQueries({ queryKey: ["zeiten"] });
  };
}

/**
 * Übernehmen — Vorbereitung und Übergabe in einem Griff, nur mit Netz.
 *
 * Beim reinen Mietgerät nach der Übergabe ist es die Rücknahme: Sie ist eine
 * eigene Aufgabe (`POST /aufgaben/nehmen`), oft Tage später und oft ein anderer.
 *
 * **Mit `grund` löst es einen Kollegen ab** (20.09.2026). Luca: *„… die
 * Möglichkeit haben, den Einsatz unter Begründung zu übernehmen, zum Beispiel
 * Krankheit."* Der Grund landet in `booking_release` und steht danach im
 * Verlauf — „Übernommen von T. Brand · Krankheit". Ohne Grund bleibt ein
 * fremder Einsatz fremd (`aufgabe.schon_genommen`).
 */
export function useUebernehmen() {
  const danach = useNachEinsatz();
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: async ({ belegungId, ruecknahme, grund, bestaetigt }: {
      belegungId: string; ruecknahme?: boolean; grund?: string;
      /** D-47: Hinweis auf die Qualifikation gelesen — trotzdem nehmen. */
      bestaetigt?: boolean;
    }) => {
      // Im Prototyp ändert sich der Stand der Sitzung — sonst täte der Knopf nichts.
      if (ATTRAPPE) return gleich(A.einsatzUebernehmen(belegungId, ruecknahme === true, grund ?? null));
      const mitGrund = grund ? { grund } : {};
      if (ruecknahme) {
        await sende<Roh>("/aufgaben/nehmen", {
          art: "ruecknahme", belegung_id: belegungId, ...mitGrund, ...(bestaetigt ? { qualifikation_bestaetigt: true } : {}),
        });
        return;
      }
      await sende<Roh>("/handy/uebernehmen", { belegung_id: belegungId, ...mitGrund });
    },
    onSuccess: () => {
      danach();
      void kunde.invalidateQueries({ queryKey: ["vorbereitung"] });
      void kunde.invalidateQueries({ queryKey: ["start"] });
    },
    onError: danach,
  });
}

/**
 * **An einen Kollegen weitergeben** (20.09.2026) — `POST /handy/uebergeben`.
 *
 * Luca: *„Du musst nicht nur zurück in den Pool geben können, sondern auch
 * eine Übergabe an einen anderen Bearbeiter qualifizieren."* Was am Einsatz
 * offen ist und mir gehört, gehört danach dem Kollegen; der Grund ist
 * freiwillig, wie beim Zurückgeben. Nur mit Netz — wie das Übernehmen.
 */
export function useEinsatzUebergeben() {
  const danach = useNachEinsatz();
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: async ({ belegungId, wer, grund }: { belegungId: string; wer: Wer; grund?: string }) => {
      if (ATTRAPPE) return gleich(A.einsatzUebergeben(belegungId, wer, grund ?? null));
      await sende<Roh>("/handy/uebergeben", {
        belegung_id: belegungId, wer: wer.id, ...(grund ? { grund } : {}),
      });
    },
    onSuccess: () => {
      danach();
      void kunde.invalidateQueries({ queryKey: ["vorbereitung"] });
      void kunde.invalidateQueries({ queryKey: ["start"] });
    },
    onError: danach,
  });
}

/** Wer diesen Einsatz an meiner Stelle nehmen könnte — `GET /handy/kollegen/:belegung`. */
export async function kollegen(belegung: string): Promise<Wer[]> {
  if (ATTRAPPE) return gleich(A.kollegenVon(belegung));
  const roh = await hole<Roh>(`/handy/kollegen/${encodeURIComponent(belegung)}`);
  return (Array.isArray(roh["kollegen"]) ? (roh["kollegen"] as Roh[]) : [])
    .map((k) => ({ id: txt(k["id"]), name: txt(k["name"]) }))
    .filter((k) => k.id !== "");
}

/** Die Liste hinter „An Kollegen weitergeben" — gefragt wird erst, wenn das Blatt aufgeht. */
export function useKollegen(belegung: string, aktiv: boolean) {
  return useQuery({
    queryKey: ["handy", "kollegen", belegung] as const,
    queryFn: () => kollegen(belegung),
    enabled: aktiv && !!belegung,
    staleTime: 60_000,
  });
}

/**
 * **Heute zum letzten Tag erklären** (B1) — `POST /handy/einsatz/abschliessen`.
 *
 * Nur mit Netz, wie das Übernehmen: Wer im Funkloch abschließt und erst abends
 * erfährt, dass die Disposition den Kran längst weiterverplant hat, steht vor
 * einem Konflikt, den niemand mehr auflösen kann. Deshalb keine Warteschlange.
 */
export function useEinsatzAbschliessen() {
  const danach = useNachEinsatz();
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: async ({ belegungId }: { belegungId: string }) => {
      // Im Prototyp rückt der geplante Schluss auf heute, wie es der Server tut.
      if (ATTRAPPE) return gleich(A.einsatzVorzeitigAbschliessen(belegungId));
      await sende<Roh>("/handy/einsatz/abschliessen", {
        belegung_id: belegungId, zeitpunkt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      danach();
      void kunde.invalidateQueries({ queryKey: ["zeiten"] });
      void kunde.invalidateQueries({ queryKey: ["planung"] });
      void kunde.invalidateQueries({ queryKey: ["start"] });
    },
    onError: danach,
  });
}

/** Der i18n-Pfad und die Werte eines Fehlers beim Übernehmen. */
export function uebernehmenFehler(e: unknown): { pfad: string; werte?: Record<string, string | number>; bestaetigbar?: true } {
  if (!(e instanceof Fehler)) return { pfad: "fehler.unbekannt" };
  // D-47: der Schein fehlt oder ist abgelaufen — ein Hinweis mit „Trotzdem nehmen".
  if (e.schluessel.startsWith("aufgabe.qualifikation_")) return { pfad: e.textPfad, werte: e.werte, bestaetigbar: true };
  if (e.schluessel === "netz" || e.status === 0) return { pfad: "einsatz.uebernehmen.ohneNetz" };
  if (e.schluessel === "aufgabe.schon_genommen") {
    return { pfad: "einsatz.uebernehmen.schonGenommen", werte: { wer: txt(e.daten["wer"]) } };
  }
  if (e.schluessel === "aufgabe.erledigt") return { pfad: "einsatz.uebernehmen.erledigt" };
  return { pfad: e.textPfad };
}

type Schritt = { belegungId: string; betreff: string };

/** Anfahrt starten — auf dem Gerät, zehn Sekunden zurücknehmbar. */
export function useLosfahren() {
  const danach = useNachEinsatz();
  return useMutation({
    mutationFn: ({ belegungId, betreff }: Schritt) => einreihen({
      art: "phase", weg: WEG.losfahren, betreff,
      nutzlast: { belegung_id: belegungId },
      halten: RUECKGAENGIG_MS, zeitfeldSpaet: "zeitpunkt",
    }),
    onSuccess: danach,
  });
}

/** „Auf der Baustelle" — schließt die Anfahrt und beginnt die Arbeit, beim Server in einem Aufruf. */
export function useAngekommen() {
  const danach = useNachEinsatz();
  return useMutation({
    mutationFn: ({ belegungId, betreff }: Schritt) => einreihen({
      art: "phase", weg: WEG.angekommen, betreff,
      nutzlast: { belegung_id: belegungId },
      halten: RUECKGAENGIG_MS, zeitfeldSpaet: "zeitpunkt",
    }),
    onSuccess: danach,
  });
}

/**
 * Ende erfassen — der eigene offene Eintrag von heute.
 *
 * Die Belegung steht mit in der Nutzlast, damit die Sicht das Ende diesem
 * Einsatz zuordnet; der Server liest sie nicht, er nimmt den Eintrag. Ohne
 * `eintragId` ist es der offene Eintrag der Person — so geht es auch, wenn der
 * Beginn selbst noch auf dem Gerät lag.
 */
export function useEndeErfassen() {
  const danach = useNachEinsatz();
  return useMutation({
    mutationFn: ({ betreff, eintragId, belegungId }: Schritt & { eintragId: string | null }) =>
      einreihen({
        art: "ausstempeln", weg: WEG.ausstempeln, betreff,
        nutzlast: { ...(eintragId ? { eintrag_id: eintragId } : {}), belegung_id: belegungId },
        zeitfeldSpaet: "zeitpunkt",
      }),
    onSuccess: danach,
  });
}

/** Der Zählerstand des Tages — morgens der Beginn, abends das Ende. */
export function useZaehlerErfassen() {
  const danach = useNachEinsatz();
  return useMutation({
    mutationFn: ({ belegungId, betreff, beginn, ende }: Schritt & { beginn?: number; ende?: number }) =>
      einreihen({
        art: "zaehler", weg: WEG.zaehler, betreff,
        nutzlast: {
          belegung_id: belegungId,
          ...(beginn !== undefined ? { zaehler_beginn: beginn } : {}),
          ...(ende !== undefined ? { zaehler_ende: ende } : {}),
        },
        zeitfeldSpaet: "erfasst_am",
      }),
    onSuccess: danach,
  });
}

/**
 * Die Bestätigung der Stunden eines Einsatztags — über das Gerät (0117/0121).
 *
 * **Immer über die Warteschlange**, mit dem Bild als Anhang: Hinter ihr liegt
 * womöglich noch das Ende des Tages, und die Bestätigung darf nicht vor ihm
 * beim Server sein (`zeit.laeuft_noch`). **`erfasst_am` geht nur mit, wenn es
 * spät wird** — dann ist `signed_at` die Uhr des Geräts und der Tag `offline`.
 * Ein zweites Mal unterschreibt der Polier nicht: Das Bild liegt im Gerät,
 * bis es abgelegt ist.
 */
export function useQuittungAufnehmen() {
  const danach = useNachEinsatz();
  return useMutation({
    mutationFn: ({ belegungId, betreff, name, bild }: Schritt & { name: string; bild: Blob | null }) =>
      einreihen({
        art: "quittung", weg: WEG.quittung, betreff,
        nutzlast: { belegung_id: belegungId, unterschrieben_von: name },
        dateien: bild ? [{ feld: "unterschrift_url", zweck: "unterschrift", blob: bild }] : [],
        zeitfeldSpaet: "erfasst_am",
      }),
    onSuccess: danach,
  });
}

/**
 * „Unterschrift nicht möglich" am Tagesende — der Grund statt der Bestätigung
 * (18.09.2026). Luca: *„Hat er keine Möglichkeit dazu, gibt er einen Grund an:
 * ‚Kunde ist nicht da.'"*
 *
 * **Über die Warteschlange** wie die Quittung, denn hinter ihm liegt womöglich
 * noch das Ende des Tages (`zeit.laeuft_noch`). Ein Name geht nicht mit: Wer
 * nicht da war, hat nichts bestätigt. Die Disposition holt es am Vorgang nach.
 */
export function useTagOhneBestaetigung() {
  const danach = useNachEinsatz();
  return useMutation({
    mutationFn: ({ belegungId, betreff, grund }: Schritt & { grund: string }) =>
      einreihen({
        art: "quittung", weg: WEG.ohneBestaetigung, betreff,
        nutzlast: { belegung_id: belegungId, grund },
        zeitfeldSpaet: "erfasst_am",
      }),
    onSuccess: danach,
  });
}

/** Einen Schritt zurücknehmen, solange er auf dem Gerät liegt. */
export function useZuruecknehmen() {
  const danach = useNachEinsatz();
  return useMutation({
    mutationFn: (id: string) => zuruecknehmen(id),
    onSuccess: danach,
  });
}

/* ══════════════ Zahlen ══════════════ */

/** `4:12` — Stunden und Minuten, für die Uhr. */
export function dauer(sekunden: number): string {
  const s = Math.max(0, Math.floor(sekunden));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

/** `9,6` — Stunden mit einer Stelle, für Summen und die Bestätigung. */
export function stunden(sekunden: number): string {
  return (Math.max(0, sekunden) / 3600).toLocaleString("de-DE", {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  });
}

/* ══════════════ Was jetzt zu tun ist ══════════════

   **Eine Hauptaktion je Zustand** (Arbeitsgrundlage, „Zustandsmaschine"),
   dazu die Nebenaktionen. Die Tabelle steht hier und nicht in der Maske,
   damit sie sich lesen lässt, ohne JSX zu lesen:

   | Zustand | Hauptaktion | Nebenaktionen |
   |---|---|---|
   | offen | Übernehmen | — |
   | vorbereiten | Vorbereiten | vorbereiten und übergeben (Mietgerät, ab dem Einsatztag), zurück in den Pool |
   | übernommen | Anfahrt starten (Mietgerät: Übergabe erfassen) | ohne Anfahrt beginnen, zurückgeben |
   | unterwegs | Auf der Baustelle | — |
   | vor Ort, noch nicht übergeben | Übergabe erfassen | Pause |
   | vor Ort | Tag abschließen (letzter Tag: Rücknahme erfassen) | Pause |
   | Pause | Pause beenden | Tag abschließen (letzter Tag: Rücknahme erfassen) |
   | Tag offen, morgens | Anfahrt starten | ohne Anfahrt beginnen |
   | Tag zu — bestätigt oder mit Grund | Zurück zu Heute (letzter Tag: Rücknahme erfassen) | — |
   | abgeschlossen, Tag noch offen | Tag abschließen | — |
   | abgeschlossen | Zurück zu Heute | Zusammenfassung |

   **Umgebaut am 18.09.2026**, Luca: *„‚Unterbrechen' — macht daraus ‚Pause'"*
   — und: *„Ich habe keinen Vorbereitungsauftrag, nur einen
   Übergabeauftrag … Das ist merkwürdig."* Drei Dinge daraus:

   1. **Die Vorbereitung kommt zuerst**, mit und ohne Bediener, auch am
      Einsatztag — und **sie wird nie übersprungen** (entschieden am
      18.09.2026). Luca: *„Wenn eine Übergabe ohne erledigte Vorbereitung
      erfolgt …, dann muss man die Fragen aus dem Vorbereitungsvorgang stellen
      und danach die Vorbereitung mit ‚mergen' sauber nachziehen."* Der
      Nebenknopf heißt deshalb **„Vorbereiten und übergeben"**: Die Übergabe
      beginnt dann mit den Fragen der Vorbereitung (`HandyProtokoll.tsx`),
      und beim Speichern wird zuerst die Vorbereitung abgeschlossen. **Es ist
      kein Weg an ihr vorbei, sondern der Weg durch sie hindurch** — deshalb
      auch nicht mehr „Bei der Übergabe vorbereiten", was sich las wie ein
      Vorbei.

   1a. **Und es gibt keinen Weg daneben — seit dem 18.09.2026, abends.**
      Luca: *„Erst die Vorbereitung — vorher kann ich gar nicht losfahren.
      … damit wir diesen harten Prozess drin haben."* Bis dahin stand beim
      **Kran mit Fahrer und eingeschalteter Übergabe** (0126) neben
      „Vorbereiten" noch „Anfahrt starten": Der Fahrer durfte losfahren,
      weil die Übergabe die Fragen auf der Baustelle nachholen würde. Dieser
      Nebenknopf ist weg. Ohne Übergabeschritt gab es ihn ohnehin nie.
      **Der Riegel steht in der API** (`dienste/vorbereitungsriegel.ts`),
      nicht hier: Diese Tabelle ist Wegweisung.
   2. **Pause statt Unterbrechen**, und in der Pause ist „Pause beenden" die
      Hauptaktion, nicht „Stunden bestätigen lassen". Den Tag schließt, wer
      ihn schließen will — nicht, wer mittags tankt.
   3. **Am letzten Tag erst die Rücknahme, dann der Tagesabschluss.** Die
      Unterschrift des Kunden ist der letzte Schritt (*„Der letzte Schritt
      muss immer die Unterschrift des Kunden sein"*).

   **Das reine Mietgerät** hat keine Zeit am Auftrag: übernommen →
   (vorbereiten) → Übergabe → beim Kunden → Rücknahme. Anfahrt und
   Baustellenzeit hängen an einer Bediener-Belegung (0108/0118).

   **Ist die Übergabe beim Einsatz mit Fahrer abgeschaltet** (0126, Vorgabe),
   fallen „Übergabe erfassen" und „Rücknahme erfassen" heraus: übernommen →
   Anfahrt → auf der Baustelle → Tag abschließen, je Tag. */

export type Handlung =
  | "uebernehmen" | "ruecknahmeUebernehmen" | "vorbereiten" | "uebergabeMitVorbereitung"
  | "losfahren" | "angekommen" | "ohneAnfahrt" | "uebergabe"
  | "ruecknahme" | "tagAbschliessen" | "pause" | "pauseBeenden" | "zusammenfassung"
  | "zurueckZuHeute" | "zurueckgeben" | "abschliessen";

/**
 * **Beim Einsatz mit Fahrer heißt die Rücknahme „Abschlussprotokoll"** (B1,
 * 18.09.2026). Luca: *„Ein End- oder Abschlussprotokoll gibt es auch bei
 * Mitfahrern. Das heißt dann nicht Rücknahme, sondern Abschlussprotokoll."*
 *
 * Der Unterschied ist keiner der Höflichkeit: Beim reinen Mietgerät **holt
 * jemand ein Gerät zurück**, beim Einsatz mit Fahrer **endet ein Einsatz** —
 * die Maschine fährt mit dem eigenen Fahrer nach Hause, zurückzunehmen ist da
 * nichts.
 *
 * In der Datenbank bleibt die Art `ruecknahme` (`handover.kind`): Das ist
 * Technik, und ein zweiter Wert daneben hieße, jede Abfrage müsste beide
 * kennen. Verschieden ist das Wort, und Wörter stehen in `de.json`.
 */
export const abschlusswort = (mitBediener: boolean, pfad: string): string =>
  mitBediener ? `${pfad}Abschluss` : pfad;

export type Handlungen = { haupt: Handlung | null; neben: Handlung[] };

/** Ist der heutige Tag zu — vom Kunden bestätigt oder mit Grund geschlossen? */
export const tagZu = (s: Pick<Einsatzlage, "einsatztag">): boolean =>
  !!(s.einsatztag?.bestaetigtAm || s.einsatztag?.ohneBestaetigungGrund);

export function handlungen(s: Sicht): Handlungen {
  /* **Zeit nur, wo der Server sie annimmt** (P9-01). Wer fährt, aber nicht
     heute, bekommt dieselben Schritte wie jemand, der nicht fährt: Anfahrt,
     Beginn und „Pause beenden" fallen weg, die Rücknahme bleibt. Die Zeiten
     eines vergangenen Einsatzes trägt die Disposition nach. */
  const fahre = s.eigeneBelegungId !== null && s.heuteStempelbar;
  const vorDemTag = s.tag.nr === null && !s.uebergabe.erledigt;
  const letzterTag = s.tag.nr === null ? s.uebergabe.erledigt : s.tag.nr >= s.tag.gesamt;
  const morgens = (s.einsatztag?.eintraege ?? 0) === 0;
  /* **Ohne Protokoll gibt es weder Übergabe noch Rücknahme zu erfassen** (0126).
     Dann ist der Weg: übernehmen → losfahren → auf der Baustelle → Ende →
     Stunden bestätigen lassen, je Tag. Das Ist setzt der Server daneben. */
  const protokoll = mitProtokoll(s);
  const rueckOffen = protokoll && s.uebergabe.erledigt && !s.ruecknahme.erledigt;
  const rueckJetzt = letzterTag && rueckOffen;
  const zu = tagZu(s);
  /* **Heute zum letzten Tag erklären** (B1, 18.09.2026). Luca: *„Ich
     entscheide heute, dass ich schon die Rückgabe mache, weil wir früher
     fertig geworden sind."* Nur beim Einsatz mit Fahrer, nur solange der
     Einsatz läuft und heute nicht ohnehin der letzte Tag ist — und nicht,
     während eine Zeit läuft: Erst ausstempeln, dann Schluss machen. */
  const frueherSchluss = s.mitBediener && !letzterTag && !s.ruecknahme.erledigt
    && s.tag.nr !== null && s.phase === null
    && (s.uebergabe.erledigt || (s.einsatztag?.eintraege ?? 0) > 0);
  const neben: Handlung[] = [];
  const mit = (haupt: Handlung | null): Handlungen => ({ haupt, neben });

  if (!s.mitBediener) {
    switch (s.zustand) {
      case "offen":
        return mit("uebernehmen");
      case "vorbereiten":
        if (!vorDemTag) neben.push("uebergabeMitVorbereitung");
        neben.push("zurueckgeben");
        return mit("vorbereiten");
      case "uebernommen":
        neben.push("zurueckgeben");
        return mit("uebergabe");
      case "vor_ort":
        if (rueckOffen && !s.ruecknahme.genommen) neben.push("ruecknahmeUebernehmen");
        if (letzterTag) return mit("ruecknahme");
        neben.push("ruecknahme");
        return mit("zurueckZuHeute");
      default:
        neben.push("zusammenfassung");
        return mit("zurueckZuHeute");
    }
  }

  switch (s.zustand) {
    case "offen":
      return mit("uebernehmen");
    case "vorbereiten":
      /* **Kein Weg neben der Vorbereitung** (18.09.2026, abends). Hier stand
         bis dahin „Anfahrt starten" für den Fahrer, wenn die Übergabe
         eingeschaltet ist — die Fragen holte dann die Baustelle nach. Luca:
         *„Erst die Vorbereitung — vorher kann ich gar nicht losfahren."*
         Geblieben ist der Weg **durch** die Vorbereitung: Wer nicht fährt,
         sondern die Maschine übergibt, beantwortet ihre Fragen im
         Übergabeformular (`uebergabeMitVorbereitung`). */
      if (!vorDemTag && !fahre && protokoll) neben.push("uebergabeMitVorbereitung");
      if (fahre) neben.push("zurueckgeben");
      return mit("vorbereiten");
    case "uebernommen":
      if (vorDemTag) return mit("zurueckZuHeute");
      if (!fahre) return mit(protokoll ? "uebergabe" : "zurueckZuHeute");
      neben.push("ohneAnfahrt", "zurueckgeben");
      return mit("losfahren");
    case "unterwegs":
      return mit(s.phase?.meins ? "angekommen" : "zurueckZuHeute");
    case "vor_ort":
      if (!s.phase?.meins) return mit(rueckJetzt ? "ruecknahme" : "zurueckZuHeute");
      neben.push("pause");
      if (protokoll && !s.uebergabe.erledigt) return mit("uebergabe");
      return mit(rueckJetzt ? "ruecknahme" : "tagAbschliessen");
    case "tag_offen":
      if (morgens) {
        if (!fahre) return mit(rueckJetzt ? "ruecknahme" : "zurueckZuHeute");
        neben.push("ohneAnfahrt");
        if (rueckJetzt) neben.push("ruecknahme");
        if (frueherSchluss) neben.push("abschliessen");
        return mit("losfahren");
      }
      if (zu) {
        if (frueherSchluss) neben.push("abschliessen");
        return mit(rueckJetzt ? "ruecknahme" : "zurueckZuHeute");
      }
      if (!fahre) return mit(rueckJetzt ? "ruecknahme" : "zurueckZuHeute");
      // Die Pause: weiter geht es mit „Pause beenden"; Schluss macht, wer will.
      neben.push(rueckJetzt ? "ruecknahme" : "tagAbschliessen");
      if (frueherSchluss) neben.push("abschliessen");
      return mit("pauseBeenden");
    case "tag_bestaetigt":
      /* **Ohne Rücknahme ist der letzte bestätigte Tag das Ende** (0126) — dann
         steht hier die Zusammenfassung, die sonst nach der Rücknahme kommt. */
      if (!protokoll && s.tag.nr !== null && s.tag.nr >= s.tag.gesamt) neben.push("zusammenfassung");
      if (frueherSchluss) neben.push("abschliessen");
      return mit(rueckJetzt ? "ruecknahme" : "zurueckZuHeute");
    case "abgeschlossen":
      if (fahre && s.phase?.art === "arbeit" && s.phase.meins) return mit("tagAbschliessen");
      if (fahre && !morgens && !zu) return mit("tagAbschliessen");
      neben.push("zusammenfassung");
      return mit("zurueckZuHeute");
  }
}

/**
 * **Bleibt nur „Zurück", dann muss dastehen, warum** (18.09.2026).
 *
 * Luca, am Telefon, vor einem Einsatz an Tag 3 von 5: *„Es steht ja, aber ich
 * kann auch nur zurück — auf heute keine Übergabe machen. Ich kann die Anfahrt
 * nicht starten, gar nichts. Entweder ist das ein kaputter Vorgang oder
 * irgendwie generell ein Bug."*
 *
 * Genau das war die Lücke: `handlungen()` kennt beim Einsatz mit Fahrer nur den
 * gebundenen Bediener (`eigeneBelegungId`). Für alle anderen — die Disposition,
 * den Kollegen, **und den Fahrer, dessen eigene Belegung fehlt** — fällt jeder
 * Zweig auf „Zurück zu Heute" zurück. Das ist meistens richtig; falsch war,
 * dass nichts dazu dastand. Ein Schirm ohne Schritt und ohne Grund sieht aus
 * wie ein kaputter Vorgang.
 *
 * Der Grund kommt aus der Lage (`dienste/einsatzlage.ts`, `hindernisFinden`) —
 * **nicht** aus der Zeile der Tagesmappe wie der alte Satz `einsatz.faehrt`:
 * Ein fremder Einsatz steht in meiner Tagesmappe gerade nicht, und deshalb
 * stand dort bisher gar nichts.
 *
 * Gezeigt wird er nur, wenn **kein** Schritt übrig ist: „Zurück zu Heute"
 * allein ist keiner, und ein Nebenknopf macht die Auskunft überflüssig.
 */
export function warumNichtsGeht(s: Sicht, h: Handlungen): { grund: Hindernis; wer: string | null } | null {
  if (h.neben.length > 0) return null;
  if (h.haupt !== null && h.haupt !== "zurueckZuHeute") return null;
  return s.hindernis;
}

/**
 * Die Stufen oben am Schirm — und welche davon gerade dran ist.
 *
 * **Die Vorbereitung ist die erste Stufe** (18.09.2026), nicht ein Posten
 * daneben. Fünf Stufen sind auf 390 px nur lesbar, wenn die Wörter kurz
 * bleiben: „Genommen" statt „Übernommen", „Beim Kunden" statt „Beim Kunden
 * vor Ort". Weggelassen wird keine — sonst fehlte genau die Stufe, an der der
 * Fahrer gerade steht.
 */
export function stufen(s: Sicht): { namen: string[]; jetzt: number } {
  const letzterTag = s.tag.nr === null ? s.uebergabe.erledigt : s.tag.nr >= s.tag.gesamt;
  if (!s.mitBediener) {
    const namen = ["uebernommen", "vorbereiten", "uebergabe", "beimKunden", "ruecknahme"];
    const jetzt = s.zustand === "offen" ? 0
      : s.zustand === "vorbereiten" ? 1
        : s.zustand === "uebernommen" ? 2
          : s.zustand === "abgeschlossen" ? 5
            : letzterTag ? 4 : 3;
    return { namen, jetzt };
  }
  /* Die Pause gehört zu „Vor Ort"; „Abschluss" ist der Tag, der zu ist —
     bestätigt oder mit Grund (18.09.2026). */
  const morgens = (s.einsatztag?.eintraege ?? 0) === 0;
  const namen = ["uebernommen", "vorbereiten", "anfahrt", "vorOrt", "abschluss"];
  const jetzt = s.zustand === "offen" ? 0
    : s.zustand === "vorbereiten" ? 1
      : s.zustand === "uebernommen" || s.zustand === "unterwegs" ? 2
        : s.zustand === "vor_ort" ? 3
          : s.zustand === "tag_offen" ? (morgens ? 2 : tagZu(s) ? 5 : 3)
            : s.zustand === "abgeschlossen" && !morgens && !tagZu(s) ? 4
              : 5;
  return { namen, jetzt };
}
